<template>
  <SogPage
    id="page"
    :title="_title"
  >
    <SogCard>
      <SogCardTitle />
      <SogCardContent>
        <PersonAddressForm
          v-show="!_person.isEmployee()"
          ref="_form"
          :visible-address="_visibleAddress"
          :invoice-address="_showInvoiceAddress"
          :person="_person"
          :address="_address"
        />
        <PersonEditFormComponent
          v-show="_person.isEmployee()"
          ref="_formPerson"
          :person="_person"
        />
      </SogCardContent>
      <SogCardActions>
        <SogCardActionButton
          :kind="SogButtonKind.Save"
          @click="save"
        >
          Speichern
        </SogCardActionButton>
        <SogCardActionButton
          :kind="SogButtonKind.Cancel"
          @click="cancel"
        >
          Abbrechen
        </SogCardActionButton>
      </SogCardActions>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import {onBeforeRouteUpdate, RouteLocationNormalizedLoaded, useRoute, useRouter} from "vue-router";
import {OrgStructureCluster} from "@/shared/model/orgStructureCluster";
import {onMounted, reactive, ref, watch} from "vue";
import PersonAddressForm from "@/modules/OrganisationStructure/components/PersonAddressEditComponent.vue";
import Person from "@/shared/model/person";
import Address from "@/shared/model/Address";
import SogCard from "@/shared/ui/SogCard.vue";
import apiOrganisationStructure from "@/modules/OrganisationStructure/api";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import {useOrganisationStructureStore} from "@/modules/OrganisationStructure/store";
import {useSpinner} from "@/shared/composables/SogSpinner";
import {useAccountStore} from "@/stores/accountStore";
import PersonDetails from "@/shared/model/personDetails";
import PersonDetailsId from "@/shared/model/personDetailsId";
import PersonEditFormComponent from "@/modules/OrganisationStructure/components/PersonEditFormComponent.vue";


const _title = ref("");
const _type = ref<OrgStructureCluster>(OrgStructureCluster.All)
const _level = ref("first")

const _person = reactive(new Person())
const _address = reactive(new Address());

const _router = useRouter()
const _form = ref<InstanceType<typeof PersonAddressForm> | null>(null)
const _formPerson = ref<InstanceType<typeof PersonEditFormComponent> | null>(null)
const _showInvoiceAddress = ref(false)
const _spinner = useSpinner()

const _organisationStructureStore = useOrganisationStructureStore();
const _accountStore = useAccountStore();

async function p_init(route: RouteLocationNormalizedLoaded) {
  const type = route.params.type as OrgStructureCluster;
  _type.value = type;

  const level = (route.params.level as string);
  _level.value = level === "" ? "first" : level
  _level.value = level

  if (_type.value === OrgStructureCluster.LossAdjuster || _type.value === OrgStructureCluster.Manufacturer || _type.value === OrgStructureCluster.Supplier) {
    _router.back()
    return
  }
  if (_level.value !== "first" && !_organisationStructureStore.createPage.ownerPersonId) {
    await _router.push("/organisationstructure/list/" + type)
    return;
  }


  switch (type) {
    case OrgStructureCluster.Customer:
      _title.value = "Kunde";
      _person.personType = "Legal"
      break;
    case OrgStructureCluster.Branch:
      _title.value = "Filiale";
      _person.personType = "Legal"
      _person.clusterTypes = [OrgStructureCluster.Branch]
      break;
    case OrgStructureCluster.BranchTemplate:
      _title.value = "Vorlagefiliale";
      _person.personType = "Legal"
      _person.name1 = "Vorlagefiliale"
      _person.clusterTypes = [OrgStructureCluster.BranchTemplate]
      break;
    case OrgStructureCluster.Employee:
      _title.value = "Mitarbeitern" + (level === "second" ? (" für " + _organisationStructureStore.createPage.ownerPersonName) : "");
      _person.personType = "Natural"
      _person.clusterTypes = [OrgStructureCluster.Employee]
      break;
    case OrgStructureCluster.Supplier:
      _title.value = "Lieferante";
      _person.personType = "Legal"
      break;
    case OrgStructureCluster.Manufacturer:
      _title.value = "Versicherer/Hersteller";
      _person.personType = "Legal"
      break;
    case OrgStructureCluster.LossAdjuster:
      _title.value = "Schadenregulierer";
      _person.personType = "Legal"
      break;
    case OrgStructureCluster.All:
      _title.value = "";
      _person.personType = "Natural"
      break;
    default:
      _title.value = "";
  }
  _title.value += " erstellen"
  //_showInvoiceAddress.value = _type.value === OrgStructureCluster.Customer
  _spinner.hide()
}


onMounted(async () => {
  const route = useRoute();
  await p_init(route)
})

onBeforeRouteUpdate(async (to) => {
  await p_init(to)
});


watch(_type, () => {
  _visibleAddress.value = !(_type.value === OrgStructureCluster.Employee || _type.value === OrgStructureCluster.BranchTemplate)
})

const _visibleAddress = ref(true)

function cancel() {
  _router.back()
}

async function save() {
  if (_form.value.validate() || _formPerson.value.validate()) {
    _spinner.show()

    try {
      const address = _visibleAddress.value ? _address : undefined

      const owner = _organisationStructureStore.createPage.ownerPersonId

      const id = await apiOrganisationStructure.update(owner ?? "", _person, address)
      if (id) {
        const b = await apiOrganisationStructure.updateDetailsCluster(owner ?? "", id, [_type.value])
        if (b) {
          _spinner.hide()

          if (_type.value === OrgStructureCluster.Customer && !_accountStore.isRoot) {
            const details = new PersonDetails({});
            details.setBooleanValue(PersonDetailsId.WithoutCreditLimit, true)
            await apiOrganisationStructure.updateDetails(owner ?? "", id, details.getValues())
          }
          await _router.back()
          return
        }
      }
    } finally {
      _spinner.hide()
    }
  }
}


</script>
<style scoped>
</style>
